'use client'

import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { translateTurkish, type TranslateResult } from '@/app/translate/actions'
import { cacheKey, hasTurkish, isQuizPath, segmentText, sentenceAround, wordAt, type CachedTranslation, type TranslateKind } from '@/lib/tr-text'
import { SpeakButtons } from '@/components/speak-buttons'
import { PopoverArrow, htmlZoom, placePopover, type PopoverSide } from '@/components/popover-arrow'
import Link from 'next/link'
import { guestSyncHref } from '@/lib/guest-lesson'

// Перевод по наведению/нажатию (.scratch/tap-translate). <TrText text="…" />
// размечает турецкие слова в строке:
//   - десктоп (есть мышь): наведение → подсказка через HOVER_DELAY_MS;
//     выделение мышью фразы → перевод выделенного;
//   - тач: тап по слову → подсказка, тап вне — закрыть. С 16.10.2026 тап
//     работает по любому турецкому слову в приложении, не только в <TrText>
//     (useTapAnywhere ниже), и слова больше не подчёркнуты пунктиром.
// Подсказка одна на всё приложение (открытие новой закрывает прежнюю),
// рендерится порталом в body поверх модалок. Её критичные стили — inline:
// на iPhone пользователя залипает старый CSS-бандл (см. PROJECT.md), и
// подсказка, завязанная на классы, развалилась бы там в мусор.

const HOVER_DELAY_MS = 280
const HIDE_DELAY_MS = 220
const POPOVER_W = 320

// ── Клиентский кэш: повторное наведение на то же слово не дёргает сервер ──
const clientCache = new Map<string, Promise<TranslateResult>>()
function requestTranslation(kind: TranslateKind, text: string, context: string): Promise<TranslateResult> {
  const key = cacheKey(kind, text, context)
  let p = clientCache.get(key)
  if (!p) {
    p = translateTurkish({ kind, text, context })
    // Ошибку и «лимит исчерпан» не кэшируем — следующая попытка должна пойти
    // на сервер заново (после понедельника или подписки перевод появится).
    p.then(
      (r) => (r.source === 'limit' || r.source === 'guest') && clientCache.delete(key),
      () => clientCache.delete(key),
    )
    clientCache.set(key, p)
  }
  return p
}

// ── Есть ли мышь (hover) — тот же приём, что в tutor-fab/календаре ──
const HOVER_QUERY = '(hover: hover) and (pointer: fine)'
function subscribeHover(cb: () => void) {
  const mq = window.matchMedia(HOVER_QUERY)
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}
function useCanHover() {
  return useSyncExternalStore(subscribeHover, () => window.matchMedia(HOVER_QUERY).matches, () => false)
}

// ── Глобальный стор подсказки (одна на приложение) ──
type PopoverState = {
  id: number
  kind: TranslateKind
  text: string
  context: string
  rect: { left: number; top: number; bottom: number; width: number }
  pinned: boolean // открыта тапом/выделением — не закрывать по уходу мыши
  anchor: string // какое именно слово подсвечивать (id экземпляра + номер сегмента)
  // Готовый разбор от вызывающего (тренажёр: lib/grammar/gloss.ts) — сервер не
  // спрашиваем, подпись источника не показываем.
  preset?: CachedTranslation
}
let popover: PopoverState | null = null
let seq = 0
const listeners = new Set<() => void>()
function emit() {
  listeners.forEach((l) => l())
}
function openPopover(p: Omit<PopoverState, 'id'>) {
  popover = { ...p, id: ++seq }
  emit()
}
function closePopover(id?: number) {
  if (id !== undefined && popover?.id !== id) return
  popover = null
  emit()
}
function subscribePopover(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
function usePopover() {
  return useSyncExternalStore(subscribePopover, () => popover, () => null)
}

let hideTimer: ReturnType<typeof setTimeout> | null = null
function cancelHide() {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
}
function scheduleHide(id: number) {
  cancelHide()
  hideTimer = setTimeout(() => closePopover(id), HIDE_DELAY_MS)
}

// Подсказка открывается только после НАСТОЯЩЕГО движения мыши над словом
// (QA 2026-09-24): после ответа под неподвижным курсором оказывалось новое
// слово, браузер присылал mouseenter (и «ненастоящий» mousemove с теми же
// координатами после перерисовки/скролла) — и через паузу карточка
// «Перевожу…» закрывала объяснение и кнопку. Слушатель на window в фазе
// перехвата помнит прошлые координаты курсора в окне (clientX/Y — при
// прокрутке они не меняются): движение без смены
// координат настоящим не считается.
let lastPointer: { x: number; y: number } | null = null
let lastMoveReal = false
let pointerTrackerOn = false
function ensurePointerTracker() {
  if (pointerTrackerOn || typeof window === 'undefined') return
  pointerTrackerOn = true
  window.addEventListener(
    'mousemove',
    (e) => {
      lastMoveReal = !!lastPointer && (lastPointer.x !== e.clientX || lastPointer.y !== e.clientY)
      lastPointer = { x: e.clientX, y: e.clientY }
    },
    { capture: true, passive: true },
  )
}

// Для экранов с шагами (урок, тренажёр): при смене шага открытая подсказка
// закрывается, даже если слово под ней осталось на месте.
export function closeTrPopover() {
  cancelHide()
  closePopover()
}

// Подсказка, привязанная к слову, закрывается, когда слово исчезло с экрана.
function useCloseOnUnmount(owns: () => boolean) {
  const ownsRef = useRef(owns)
  useEffect(() => {
    ownsRef.current = owns
  })
  useEffect(
    () => () => {
      if (ownsRef.current()) closePopover()
    },
    [],
  )
}

function rectOf(el: Element | Range) {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, bottom: r.bottom, width: r.width }
}


export function TrText({ text, style, className }: { text: string; style?: CSSProperties; className?: string }): ReactNode {
  const canHover = useCanHover()
  const rootRef = useRef<HTMLSpanElement>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const uid = useId()
  // Подписка только на «своё» подсвеченное слово: на странице «Грамматика»
  // тысячи слов в сотнях <TrText>, и перерисовывать все на каждое открытие
  // подсказки было бы заметно медленно на телефоне.
  const activeAnchor = useSyncExternalStore(
    subscribePopover,
    () => (popover?.anchor.startsWith(uid + ':') ? popover.anchor : null),
    () => null,
  )

  useEffect(() => {
    ensurePointerTracker()
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
    }
  }, [])
  useCloseOnUnmount(() => !!popover?.anchor.startsWith(uid + ':'))

  if (!hasTurkish(text)) {
    return (
      <span className={className} style={style}>
        {text}
      </span>
    )
  }

  const segments = segmentText(text)
  // В смешанном тексте (объяснения правил, разбор) турецкие слова выделены
  // тёплым цветом и толщиной, иначе на телефоне они сливаются с русскими.
  // Целиком турецкие строки (реплики, фразы) остаются как были.
  const mixed = /[а-яё]/i.test(text)

  function open(target: HTMLElement, word: string, start: number, pinned: boolean) {
    cancelHide()
    openPopover({ kind: 'word', text: word, context: sentenceAround(text, start), rect: rectOf(target), pinned, anchor: `${uid}:${start}` })
  }

  function handleMouseUp() {
    if (!canHover) return
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || !rootRef.current) return
    const selected = sel.toString().trim()
    if (!selected || !/\s/.test(selected) || !hasTurkish(selected)) return
    const range = sel.getRangeAt(0)
    if (!rootRef.current.contains(range.commonAncestorContainer)) return
    cancelHide()
    openPopover({ kind: 'phrase', text: selected, context: '', rect: rectOf(range), pinned: true, anchor: '' })
  }

  return (
    <span ref={rootRef} className={className} style={style} onMouseUp={handleMouseUp}>
      {segments.map((seg, i) =>
        seg.word ? (
          <span
            key={i}
            data-tkt-word=""
            onMouseMove={
              canHover
                ? (e) => {
                    // Подсказка уже у этого слова — только отменить её скрытие.
                    if (popover?.anchor === `${uid}:${seg.start}`) return cancelHide()
                    // Таймер уже идёт — не перезапускаем.
                    if (!lastMoveReal || hoverTimer.current) return
                    const el = e.currentTarget
                    hoverTimer.current = setTimeout(() => {
                      hoverTimer.current = null
                      if (window.getSelection()?.toString()) return // идёт выделение фразы
                      open(el, seg.text, seg.start, false)
                    }, HOVER_DELAY_MS)
                  }
                : undefined
            }
            onMouseLeave={
              canHover
                ? () => {
                    if (hoverTimer.current) clearTimeout(hoverTimer.current)
                    hoverTimer.current = null
                    if (popover && !popover.pinned) scheduleHide(popover.id)
                  }
                : undefined
            }
            onClick={
              canHover
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    open(e.currentTarget, seg.text, seg.start, true)
                  }
            }
            style={{
              cursor: canHover ? 'help' : 'pointer',
              borderRadius: 4,
              transition: 'background 0.12s ease',
              background:
                activeAnchor === `${uid}:${seg.start}`
                  ? 'oklch(0.78 0.14 25 / 0.22)'
                  : undefined,
              // Пунктира на тач-экранах больше нет (владелец 16.10.2026): тап
              // переводит любое турецкое слово, отмечать нажимаемые незачем.
              color: mixed ? 'var(--tk-word)' : undefined,
              fontWeight: mixed ? 600 : undefined,
            }}
          >
            {seg.text}
          </span>
        ) : (
          <Fragment key={i}>{seg.text}</Fragment>
        ),
      )}
    </span>
  )
}

// Та же подсказка для произвольной разметки одного слова: разбор морфем в
// грамматике красит части слова по-своему, TrText такую разметку не принимает.
// <TrHover word="gelmek" context="gelmek istiyorum">…цветные части…</TrHover>
export function TrHover({
  word,
  context = '',
  preset,
  children,
  style,
}: {
  word: string
  context?: string
  preset?: CachedTranslation
  children: ReactNode
  style?: CSSProperties
}): ReactNode {
  const canHover = useCanHover()
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const uid = useId()
  const active = useSyncExternalStore(subscribePopover, () => popover?.anchor === uid, () => false)

  useEffect(() => {
    ensurePointerTracker()
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
    }
  }, [])
  useCloseOnUnmount(() => popover?.anchor === uid)

  if (!hasTurkish(word)) {
    return <span style={style}>{children}</span>
  }

  function open(target: HTMLElement, pinned: boolean) {
    cancelHide()
    openPopover({ kind: 'word', text: word, context: context || word, rect: rectOf(target), pinned, anchor: uid, preset })
  }

  return (
    <span
      data-tkt-word=""
      onMouseMove={
        canHover
          ? (e) => {
              if (popover?.anchor === uid) return cancelHide()
              if (!lastMoveReal || hoverTimer.current) return
              const el = e.currentTarget
              hoverTimer.current = setTimeout(() => {
                hoverTimer.current = null
                if (window.getSelection()?.toString()) return
                open(el, false)
              }, HOVER_DELAY_MS)
            }
          : undefined
      }
      onMouseLeave={
        canHover
          ? () => {
              if (hoverTimer.current) clearTimeout(hoverTimer.current)
              hoverTimer.current = null
              if (popover && !popover.pinned) scheduleHide(popover.id)
            }
          : undefined
      }
      onClick={
        canHover
          ? undefined
          : (e) => {
              e.stopPropagation()
              open(e.currentTarget, true)
            }
      }
      style={{
        cursor: canHover ? 'help' : 'pointer',
        borderRadius: 4,
        transition: 'background 0.12s ease',
        background: active ? 'oklch(0.78 0.14 25 / 0.22)' : undefined,
        ...style,
      }}
    >
      {children}
    </span>
  )
}

// ── Тап по любому турецкому слову (тач-экраны) ──
// Слово ищется по точке тапа (caretPositionFromPoint / caretRangeFromPoint), а
// не по разметке, поэтому работает в любом тексте приложения. Пропускаем всё,
// где у тапа своё действие (кнопки, ссылки, поля, плитки с cursor: pointer),
// слова <TrText> (у них свой обработчик), саму подсказку и задания до ответа:
// экраны из isQuizPath и всё внутри [data-no-translate].
const TAP_SKIP =
  'a,button,input,textarea,select,label,summary,[contenteditable="true"],[contenteditable=""],[role="button"],[role="link"],[role="tab"],[role="option"],[role="switch"],[role="checkbox"],[role="radio"],[data-tkt-word],[data-tkt-popover],[data-no-translate]'

function caretAt(x: number, y: number): { node: Text; offset: number } | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  const pos = doc.caretPositionFromPoint?.(x, y)
  if (pos) return pos.offsetNode.nodeType === Node.TEXT_NODE ? { node: pos.offsetNode as Text, offset: pos.offset } : null
  const range = doc.caretRangeFromPoint?.(x, y)
  if (range && range.startContainer.nodeType === Node.TEXT_NODE) return { node: range.startContainer as Text, offset: range.startOffset }
  return null
}

function useTapAnywhere() {
  const canHover = useCanHover()
  useEffect(() => {
    if (canHover) return
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0) return
      const target = e.target
      if (!(target instanceof Element) || target.closest(TAP_SKIP)) return
      if (getComputedStyle(target).cursor === 'pointer') return
      if (isQuizPath(window.location.pathname)) return
      if (window.getSelection()?.toString()) return
      const caret = caretAt(e.clientX, e.clientY)
      if (!caret || !target.contains(caret.node)) return
      const hit = wordAt(caret.node.data, caret.offset)
      if (!hit) return
      // Каретка находится и по тапу в пустое место рядом с текстом: проверяем,
      // что палец действительно на слове (с запасом в пару пикселей).
      const range = document.createRange()
      range.setStart(caret.node, hit.start)
      range.setEnd(caret.node, hit.start + hit.word.length)
      const PAD = 4
      const onWord = Array.from(range.getClientRects()).some(
        (r) => e.clientX >= r.left - PAD && e.clientX <= r.right + PAD && e.clientY >= r.top - PAD && e.clientY <= r.bottom + PAD,
      )
      if (!onWord) return
      // Контекст для перевода: предложение из ближайшего блока текста.
      const block = caret.node.parentElement?.closest('p,li,td,th,dd,dt,h1,h2,h3,h4,blockquote,div') ?? caret.node.parentElement
      let context = caret.node.data
      let index = hit.start
      if (block) {
        const before = document.createRange()
        before.setStart(block, 0)
        before.setEnd(caret.node, hit.start)
        context = block.textContent ?? context
        index = before.toString().length
      }
      cancelHide()
      openPopover({ kind: 'word', text: hit.word, context: sentenceAround(context, index), rect: rectOf(range), pinned: true, anchor: `tap:${hit.word}:${index}` })
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [canHover])
}

// ── Сама подсказка ──
// Монтируется ОДИН раз в app/layout.tsx — рядом с TutorFab.
export function TrPopoverHost() {
  const p = usePopover()
  useTapAnywhere()
  if (typeof document === 'undefined' || !p) return null
  return createPortal(<Popover key={p.id} state={p} />, document.body)
}

type PopResult = TranslateResult | (CachedTranslation & { source: 'engine'; notice?: undefined })
type Loaded = { status: 'loading' } | { status: 'ok'; result: PopResult } | { status: 'error' }

// Недельный лимит AI-переводов исчерпан: текст сервера + ссылка на тарифы
// (компактная версия LimitNotice — попап узкий, кнопка туда не помещается).
function LimitLine({ text, small = false }: { text?: string; small?: boolean }) {
  return (
    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', fontSize: small ? 13 : 14, color: 'oklch(0.8 0.03 240)', lineHeight: 1.4 }}>
      {text && <span>{text}</span>}
      <Link href="/plans" className={small ? 'tk-pill tk-pill--sm tk-pill--gold text-[15px]' : 'tk-pill tk-pill--sm tk-pill--gold'}>
        Больше AI в подписке
      </Link>
    </div>
  )
}

// Гость в уроке без входа (source 'guest'): в словаре и кэше перевода нет, модель
// после входа. Приглашение войти не чаще раза за урок (план .scratch/guest-mode,
// шаг 5), дальше просто "Нет перевода". Урок = адрес страницы.
let guestInviteShownAt: string | null = null
const currentPath = () => (typeof window === 'undefined' ? '' : window.location.pathname)

function GuestLine() {
  const [invite] = useState(() => guestInviteShownAt !== currentPath())
  useEffect(() => {
    if (invite) guestInviteShownAt = currentPath()
  }, [invite])
  if (!invite) return <div style={{ marginTop: 6, color: C.muted }}>Нет перевода.</div>
  const next = typeof window === 'undefined' ? '/' : guestSyncHref(window.location.pathname + window.location.search)
  return (
    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', fontSize: 16, color: C.muted, lineHeight: 1.4 }}>
      <span>Войдите, и откроется перевод любого слова.</span>
      <Link href={`/auth/sign-up?from=guest-translate&next=${encodeURIComponent(next)}`} className="tk-pill tk-pill--sm tk-pill--gold">
        Войти
      </Link>
    </div>
  )
}

function useTranslation(kind: TranslateKind, text: string, context: string, enabled = true): Loaded {
  const [state, setState] = useState<Loaded>({ status: 'loading' })
  useEffect(() => {
    if (!enabled) return
    let alive = true
    requestTranslation(kind, text, context).then(
      (result) => alive && setState({ status: 'ok', result }),
      () => alive && setState({ status: 'error' }),
    )
    return () => {
      alive = false
    }
  }, [kind, text, context, enabled])
  return state
}

const C = {
  bg: 'linear-gradient(165deg, oklch(0.27 0.05 254 / 0.98) 0%, oklch(0.17 0.035 254 / 0.99) 100%)',
  border: 'oklch(0.78 0.14 228 / 0.55)',
  text: 'oklch(0.97 0.005 240)',
  muted: 'oklch(0.8 0.03 240)',
  gold: 'oklch(0.8 0.13 25)',
  ice: 'oklch(0.86 0.11 225)',
}

function Popover({ state }: { state: PopoverState }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number; arrowX: number; side: PopoverSide } | null>(null)
  const [showSentence, setShowSentence] = useState(false)
  const fetched = useTranslation(state.kind, state.text, state.context, !state.preset)
  const main: Loaded = state.preset ? { status: 'ok', result: { ...state.preset, source: 'engine' } } : fetched
  const sentence = useTranslation('phrase', state.context, '', showSentence && state.kind === 'word' && !!state.context)

  // Позиция: под словом, если снизу хватает места, иначе над ним; по
  // горизонтали — не за края экрана (на 390px ширины это важно).
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setPos(placePopover(state.rect, Math.min(POPOVER_W, window.innerWidth / htmlZoom() - 24), el.offsetHeight, 12))
  }, [state, main.status, sentence.status, showSentence])

  // Закрытие: тап/клик вне подсказки, Esc, прокрутка (позиция устарела бы).
  useEffect(() => {
    function onDown(e: PointerEvent) {
      const t = e.target as Node
      if (ref.current?.contains(t)) return
      if (t instanceof Element && t.closest('[data-tkt-word]')) return
      closePopover(state.id)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closePopover(state.id)
    }
    function onScroll(e: Event) {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return
      closePopover(state.id)
    }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [state.id])

  const r = main.status === 'ok' ? main.result : null

  return (
    <div
      ref={ref}
      role="dialog"
      data-tkt-popover=""
      aria-label={`Перевод: ${state.text}`}
      onMouseEnter={cancelHide}
      onMouseLeave={() => !state.pinned && scheduleHide(state.id)}
      style={{
        position: 'fixed',
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        zIndex: 9999,
        width: `min(${POPOVER_W}px, calc(var(--tk-vw, 100vw) - 24px))`,
        padding: '12px 14px',
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        background: C.bg,
        boxShadow:
          'inset 0 1px 0 oklch(1 0 0 / 0.07), 0 0 0 1px oklch(0.58 0.19 258 / 0.25), 0 16px 34px -12px oklch(0 0 0 / 0.8), 0 0 24px -6px oklch(0.58 0.19 258 / 0.45)',
        color: C.text,
        fontSize: 16,
        lineHeight: 1.4,
        textAlign: 'left',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {pos && <PopoverArrow x={pos.arrowX} side={pos.side} border={C.border} />}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: C.ice, wordBreak: 'break-word' }}>
          {state.kind === 'phrase' && state.text.length > 80 ? `${state.text.slice(0, 80)}...` : state.text}
        </span>
        {r && r.source !== 'limit' && r.source !== 'guest' && r.source !== 'engine' && (
          <span style={{ flexShrink: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em', color: C.muted }}>
            {r.source === 'dictionary' ? 'словарь' : 'AI-перевод'}
          </span>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <SpeakButtons text={state.text} size={14} label="Послушать" />
      </div>

      {main.status === 'loading' && <div style={{ marginTop: 6, color: C.muted }}>Перевожу...</div>}
      {main.status === 'error' && <div style={{ marginTop: 6, color: 'oklch(0.78 0.12 25)' }}>Сервис перевода сейчас не отвечает. Наведите ещё раз чуть позже.</div>}
      {r?.source === 'limit' && <LimitLine text={r.notice} />}
      {r?.source === 'guest' && <GuestLine />}

      {r && r.source !== 'limit' && r.source !== 'guest' && (
        <>
          <div style={{ marginTop: 4, fontSize: 17, fontWeight: 600, color: C.gold }}>{r.translation}</div>

          {r.lemma && (
            <div style={{ marginTop: 6, fontSize: 16, color: C.muted }}>
              Начальная форма: <b style={{ color: C.text }}>{r.lemma}</b>
              {r.lemmaTranslation ? ` - ${r.lemmaTranslation}` : ''}
            </div>
          )}

          {r.breakdown.length > 1 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {r.breakdown.map((b, i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    padding: '4px 8px',
                    borderRadius: 8,
                    background: 'oklch(1 0 0 / 0.06)',
                    border: '1px solid oklch(1 0 0 / 0.1)',
                  }}
                >
                  <span style={{ fontWeight: 700, color: C.ice, fontSize: 16 }}>{i === 0 ? b.part : `-${b.part.replace(/^-/, '')}`}</span>
                  <span style={{ fontSize: 15, color: C.muted }}>{b.meaningRu}</span>
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {state.kind === 'word' && state.context && state.context.trim() !== state.text.trim() && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid oklch(1 0 0 / 0.08)' }}>
          {!showSentence ? (
            <button type="button" onClick={() => setShowSentence(true)} className="tk-pill tk-pill--sm">
              Перевести предложение
            </button>
          ) : sentence.status === 'loading' ? (
            <div style={{ fontSize: 16, color: C.muted }}>Перевожу предложение...</div>
          ) : sentence.status === 'ok' && sentence.result.source === 'limit' ? (
            <LimitLine text={sentence.result.notice} small />
          ) : sentence.status === 'ok' && sentence.result.source === 'guest' ? (
            <GuestLine />
          ) : sentence.status === 'ok' ? (
            <div style={{ fontSize: 16, color: C.text }}>{sentence.result.translation}</div>
          ) : (
            <div style={{ fontSize: 16, color: 'oklch(0.78 0.12 25)' }}>Не удалось перевести предложение.</div>
          )}
        </div>
      )}
    </div>
  )
}
